import React, { FC, useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  TrendingUp,
  TrendingDown,
  Person as PersonIcon,
} from '@mui/icons-material'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalPageHeader } from '@/apps/legacy/components/portal/PortalPageHeader'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import {
  MetricsLineChart,
  MetricsBarChart,
  formatDurationToMinutes,
} from '@/apps/legacy/components/portal/MetricsChart'
import {
  useMetricsSummaryQuery,
  useMetricsAnalyticsQuery,
  generateDateRangeArray,
  mapDataToDateRange,
  buildPharmacistAnalyticsParams,
} from '@/features/internal/metrics'
import { usePharmacistsQuery } from '@/features/auth'
import { formatPercentage } from '@/shared/utils/formatters'
import dayjs, { Dayjs } from 'dayjs'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

interface KPICardProps {
  title: string
  value: string | number
  unit?: string
  previousValue?: string
  change?: number
  changeLabel?: string
}

const KPICard: FC<KPICardProps> = ({
  title,
  value,
  unit = '',
  previousValue,
  change,
  changeLabel,
}) => {
  const isPositive = change && change > 0
  const changeColor = isPositive
    ? colorTokens.status.success
    : colorTokens.special.changeNegative

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        backgroundColor: colorTokens.background.paper,
        border: `1px solid ${colorTokens.border.light}`,
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: colorTokens.text.secondary, mb: 1 }}
      >
        {title}
      </Typography>
      <Typography
        variant="h4"
        sx={{
          color: colorTokens.text.primary,
          mb: 1,
        }}
      >
        {value}
        {unit && (
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              color: colorTokens.text.secondary,
            }}
          >
            {unit}
          </Typography>
        )}
      </Typography>
      {previousValue && (
        <Typography
          variant="body2"
          sx={{ color: colorTokens.grey[500], mb: 0.5 }}
        >
          前月: {previousValue}
        </Typography>
      )}
      {change !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isPositive ? (
            <TrendingUp sx={{ fontSize: 16, color: changeColor }} />
          ) : (
            <TrendingDown sx={{ fontSize: 16, color: changeColor }} />
          )}
          <Typography variant="body2" sx={{ color: changeColor }}>
            {isPositive ? '+' : ''}
            {change}
            {changeLabel || '%'}
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

export const GuidanceAnalysisPage: FC = () => {
  const [selectedPharmacistState, setSelectedPharmacist] = useState<
    string | null
  >(null)
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(1, 'month')
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs())

  // 薬剤師一覧取得
  const { pharmacists } = usePharmacistsQuery()

  // 選択された薬剤師（未選択時は最初の薬剤師をデフォルトとして使用）
  const selectedPharmacist = useMemo(() => {
    if (selectedPharmacistState !== null) {
      return selectedPharmacistState
    }
    // 初期値として最初の薬剤師を使用
    if (pharmacists && pharmacists.pharmacists.length > 0) {
      return pharmacists.pharmacists[0].id
    }
    return null
  }, [selectedPharmacistState, pharmacists])

  // 薬剤師選択リストの作成（メモ化）
  const pharmacistList = useMemo(() => {
    const list = pharmacists?.pharmacists || []
    return [
      { id: '', name: '全員' },
      ...list
        .filter((p) => p.display_name && p.display_name.trim() !== '')
        .map((p) => ({
          id: p.id,
          name: p.display_name,
        })),
    ]
  }, [pharmacists])

  // リクエストパラメータを構築
  const summaryParams = useMemo(() => {
    if (!startDate || !endDate) return null
    return buildPharmacistAnalyticsParams(
      startDate.format('YYYY-MM-DD'),
      endDate.format('YYYY-MM-DD'),
      selectedPharmacist || '',
      true // include_previous_period
    )
  }, [startDate, endDate, selectedPharmacist])

  const analyticsParams = useMemo(() => {
    if (!startDate || !endDate) return null
    return {
      ...buildPharmacistAnalyticsParams(
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD'),
        selectedPharmacist || '',
        false
      ),
      period: 'daily' as const,
    }
  }, [startDate, endDate, selectedPharmacist])

  // サマリーデータ取得
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useMetricsSummaryQuery({
    params: summaryParams!,
    enabled: !!summaryParams,
  })

  // 分析データ取得
  const {
    data: analytics,
    isLoading: analyticsLoading,
    error: analyticsError,
  } = useMetricsAnalyticsQuery({
    params: analyticsParams!,
    enabled: !!analyticsParams,
  })

  const isInitialLoading = summaryLoading || analyticsLoading
  const errors = [summaryError?.message, analyticsError?.message].filter(
    Boolean
  )
  const error = errors.length > 0 ? errors.join('、') : null

  const handlePharmacistChange = (event: { target: { value: string } }) => {
    setSelectedPharmacist(event.target.value)
  }

  // データ変換関数（メモ化）
  const formatDurationTrend = useMemo(() => {
    if (!analytics?.trends?.durations || !startDate || !endDate) return []

    const dateRange = generateDateRangeArray(startDate, endDate)
    const data = mapDataToDateRange(
      dateRange,
      analytics?.trends?.durations,
      (item) => item.avg_duration || 0,
      0
    )

    return data.map((item) => ({
      日付: item.日付,
      指導時間: item.value,
    }))
  }, [analytics?.trends?.durations, startDate, endDate])

  const formatHourlyPerformance = useMemo(() => {
    if (!analytics?.distributions?.hourly_pattern) return []
    return Object.entries(analytics?.distributions?.hourly_pattern).map(
      ([timeRange, data]) => ({
        時間帯: timeRange,
        平均指導時間: data.avg_duration,
        件数: data.count,
      })
    )
  }, [analytics?.distributions?.hourly_pattern])

  return (
    <PortalLayout>
      <Box sx={{ position: 'relative' }}>
        {/* 初期ローディング表示（固定位置） */}
        {isInitialLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 2,
              p: 2,
            }}
          >
            <LoadingIndicator />
          </Box>
        )}

        <PortalPageHeader title="指導分析" />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* フィルター */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            backgroundColor: colorTokens.background.paper,
            borderRadius: 2,
            border: `1px solid ${colorTokens.border.light}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            p: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>薬剤師</InputLabel>
              <Select
                value={selectedPharmacist || ''}
                onChange={handlePharmacistChange}
                label="薬剤師"
                size="small"
                displayEmpty
                startAdornment={
                  <PersonIcon
                    sx={{ mr: 1, color: colorTokens.text.secondary }}
                  />
                }
                renderValue={(value) => {
                  if (value === '') return '全員'
                  const pharmacist = pharmacistList.find((p) => p.id === value)
                  return pharmacist ? pharmacist.name : '未選択'
                }}
              >
                {pharmacistList.map((pharmacist) => (
                  <MenuItem key={pharmacist.id} value={pharmacist.id}>
                    {pharmacist.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="開始日"
                value={startDate}
                onChange={setStartDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 200 },
                  },
                }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="終了日"
                value={endDate}
                onChange={setEndDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 200 },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Paper>

        {/* 基本指標 */}
        {summary && (
          <>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: colorTokens.text.primary,
              }}
            >
              基本指標
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard
                  title="平均指導時間"
                  value={`${Math.floor(summary.average_duration / 60)}分${
                    summary.average_duration % 60
                  }秒`}
                  unit=""
                  previousValue={
                    summary.previous_period &&
                    summary.previous_period.average_duration > 0
                      ? `${Math.floor(
                          summary.previous_period.average_duration / 60
                        )}分${summary.previous_period.average_duration % 60}秒`
                      : undefined
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard
                  title="1剤あたり時間"
                  value={summary.time_per_medication?.toFixed(0) || '-'}
                  unit="秒/剤"
                  previousValue={
                    summary.previous_period &&
                    summary.previous_period.time_per_medication &&
                    summary.previous_period.time_per_medication > 0
                      ? summary.previous_period.time_per_medication.toFixed(0)
                      : undefined
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard
                  title="平均やり取り回数"
                  value={summary.average_exchange_count.toFixed(1)}
                  unit="回"
                  previousValue={
                    summary.previous_period &&
                    summary.previous_period.average_exchange_count &&
                    summary.previous_period.average_exchange_count > 0
                      ? summary.previous_period.average_exchange_count.toFixed(
                          1
                        )
                      : undefined
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard
                  title="話速"
                  value={summary.speech_rate?.toFixed(0) || '-'}
                  unit="文字/分"
                  previousValue={
                    summary.previous_period &&
                    summary.previous_period.speech_rate &&
                    summary.previous_period.speech_rate > 0
                      ? summary.previous_period.speech_rate.toFixed(0)
                      : undefined
                  }
                />
              </Grid>
            </Grid>

            {/* 発話比率 */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                backgroundColor: colorTokens.background.paper,
                borderRadius: 2,
                border: `1px solid ${colorTokens.border.light}`,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: colorTokens.text.primary,
                }}
              >
                発話比率
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      height: 40,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: `1px solid ${colorTokens.border.light}`,
                    }}
                  >
                    {summary.speech_ratio ? (
                      <>
                        <Box
                          sx={{
                            width: `${summary.speech_ratio.pharmacist_ratio}%`,
                            bgcolor: colorTokens.blue[600],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                          }}
                        >
                          薬剤師{' '}
                          {formatPercentage(
                            summary.speech_ratio.pharmacist_ratio
                          )}
                          %
                        </Box>
                        <Box
                          sx={{
                            width: `${summary.speech_ratio.patient_ratio}%`,
                            bgcolor: colorTokens.blue[200],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: colorTokens.blue[700],
                          }}
                        >
                          患者{' '}
                          {formatPercentage(summary.speech_ratio.patient_ratio)}
                          %
                        </Box>
                      </>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          bgcolor: colorTokens.grey[100],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: colorTokens.grey[500],
                        }}
                      >
                        データなし
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </>
        )}

        {/* 指導時間分析 */}
        {analytics && (
          <>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: colorTokens.background.paper,
                    borderRadius: 2,
                    border: `1px solid ${colorTokens.border.light}`,
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    p: 3,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: colorTokens.text.primary,
                      mb: 2,
                    }}
                  >
                    平均指導時間の推移
                  </Typography>
                  <MetricsLineChart
                    data={formatDurationTrend}
                    dataKey="指導時間"
                    xAxisKey="日付"
                    height={350}
                    color={colorTokens.blue[600]}
                    yAxisTickFormatter={formatDurationToMinutes}
                  />
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: colorTokens.background.paper,
                    borderRadius: 2,
                    border: `1px solid ${colorTokens.border.light}`,
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    p: 3,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: colorTokens.text.primary,
                      mb: 2,
                    }}
                  >
                    時間帯別の平均指導時間
                  </Typography>
                  <MetricsBarChart
                    data={formatHourlyPerformance}
                    dataKey="平均指導時間"
                    xAxisKey="時間帯"
                    height={350}
                    color={colorTokens.blue[600]}
                    yAxisTickFormatter={formatDurationToMinutes}
                  />
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </PortalLayout>
  )
}
